import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import IssuerProtectedRoute from './components/IssuerProtectedRoute';
import PassportPublicAnalytics from './components/PassportPublicAnalytics';

const Landing = lazy(() => import('./pages/Landing'));
const Technology = lazy(() => import('./pages/Technology'));
const ForCarriers = lazy(() => import('./pages/ForCarriers'));
const ForBrokers = lazy(() => import('./pages/ForBrokers'));
const CarrierVerification = lazy(() => import('./pages/CarrierVerification'));
const LearnWhatBrokersAskFor = lazy(() => import('./pages/learn/LearnWhatBrokersAskFor'));
const LearnFmcsaVsPassport = lazy(() => import('./pages/learn/LearnFmcsaVsPassport'));
const LearnSharingCredentialsSafely = lazy(() => import('./pages/learn/LearnSharingCredentialsSafely'));
const EnforcementEntry = lazy(() => import('./pages/EnforcementEntry'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ContinueRegistration = lazy(() => import('./pages/ContinueRegistration'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const PassportAppLayout = lazy(() => import('./components/PassportAppLayout'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Enforcement = lazy(() => import('./pages/Enforcement'));
const Account = lazy(() => import('./pages/Account'));
const Settings = lazy(() => import('./pages/Settings'));
const Viewer = lazy(() => import('./pages/Viewer'));
const ConnectionsPartners = lazy(() => import('./pages/ConnectionsPartners'));
const OutgoingConnectionRequests = lazy(() => import('./pages/OutgoingConnectionRequests'));
const CarrierPassportCommercial = lazy(() => import('./pages/CarrierPassportCommercial'));
const CarrierPassportFmcsaUnregistered = lazy(() => import('./pages/CarrierPassportFmcsaUnregistered'));
const AccessRequests = lazy(() => import('./pages/AccessRequests'));
const CarrierIssuerSubmissions = lazy(() => import('./pages/CarrierIssuerSubmissions'));
const DocumentPackageSend = lazy(() => import('./pages/DocumentPackageSend'));
const DocumentPackageDetail = lazy(() => import('./pages/DocumentPackageDetail'));
const CmvPage = lazy(() => import('./pages/CmvPage'));
const CmvDailyPage = lazy(() => import('./pages/CmvDailyPage'));
const IssuerHub = lazy(() => import('./pages/IssuerHub'));
const IssuerSubmit = lazy(() => import('./pages/IssuerSubmit'));
const IssuerSubmitAccessEntry = lazy(() => import('./pages/IssuerSubmitAccessEntry'));
const IssuerCarrierLookup = lazy(() => import('./pages/IssuerCarrierLookup'));
const IssuerRegister = lazy(() => import('./pages/IssuerRegister'));
const IssuerLogin = lazy(() => import('./pages/IssuerLogin'));
const IssuerHome = lazy(() => import('./pages/IssuerHome'));
const IssuerProfile = lazy(() => import('./pages/IssuerProfile'));
const IntegrationPartnerDashboard = lazy(() => import('./pages/IntegrationPartnerDashboard'));
const FleetRegister = lazy(() => import('./pages/FleetRegister'));
const FleetHome = lazy(() => import('./pages/FleetHome'));
const FleetDriverAcceptInvite = lazy(() => import('./pages/FleetDriverAcceptInvite'));
const FleetDriverSummary = lazy(() => import('./pages/FleetDriverSummary'));
const LegalPrivacy = lazy(() => import('./pages/legal/LegalPrivacy'));
const LegalPrivacySettings = lazy(() => import('./pages/legal/LegalPrivacySettings'));
const LegalTerms = lazy(() => import('./pages/legal/LegalTerms'));
const LegalRefundPolicy = lazy(() => import('./pages/legal/LegalRefundPolicy'));
const LegalAccessibility = lazy(() => import('./pages/legal/LegalAccessibility'));
const LegalSupport = lazy(() => import('./pages/legal/LegalSupport'));

function Fallback() {
  return <div style={{ padding: '2rem' }}>Loading…</div>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PassportPublicAnalytics />
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/technology" element={<Technology />} />
            <Route path="/for-carriers" element={<ForCarriers />} />
            <Route path="/for-brokers" element={<ForBrokers />} />
            <Route path="/carrier-verification" element={<CarrierVerification />} />
            <Route path="/learn/what-brokers-ask-for" element={<LearnWhatBrokersAskFor />} />
            <Route path="/learn/fmcsa-vs-passport" element={<LearnFmcsaVsPassport />} />
            <Route path="/learn/sharing-credentials-safely" element={<LearnSharingCredentialsSafely />} />
            <Route path="/enforcement-entry" element={<EnforcementEntry />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/fleet/register" element={<FleetRegister />} />
            <Route path="/fleet/accept-invite" element={<FleetDriverAcceptInvite />} />
            <Route path="/continue" element={<ContinueRegistration />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/legal/privacy" element={<LegalPrivacy />} />
            <Route path="/legal/privacy-settings" element={<LegalPrivacySettings />} />
            <Route path="/legal/terms" element={<LegalTerms />} />
            <Route path="/legal/refund-cancellation" element={<LegalRefundPolicy />} />
            <Route path="/legal/accessibility" element={<LegalAccessibility />} />
            <Route path="/legal/support" element={<LegalSupport />} />
            <Route path="/issuer" element={<IssuerHub />} />
            <Route path="/issuer/submit" element={<IssuerSubmit />} />
            <Route path="/issuer/request-access" element={<IssuerSubmitAccessEntry />} />
            <Route path="/issuer/lookup" element={<IssuerCarrierLookup />} />
            <Route path="/issuer/register" element={<IssuerRegister />} />
            <Route path="/issuer/login" element={<IssuerLogin />} />
            <Route path="/integration-partner" element={<IntegrationPartnerDashboard />} />
            <Route
              path="/issuer/home"
              element={
                <IssuerProtectedRoute>
                  <IssuerHome />
                </IssuerProtectedRoute>
              }
            />
            <Route
              path="/issuer/profile"
              element={
                <IssuerProtectedRoute>
                  <IssuerProfile />
                </IssuerProtectedRoute>
              }
            />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route
              element={
                <ProtectedRoute>
                  <PassportAppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/connections" element={<ConnectionsPartners />} />
              <Route path="/connection-requests/outgoing" element={<OutgoingConnectionRequests />} />
              <Route path="/carrier-passport/fmcsa" element={<CarrierPassportFmcsaUnregistered />} />
              <Route path="/carrier-passport/:carrierUserId" element={<CarrierPassportCommercial />} />
              <Route path="/access-requests" element={<AccessRequests />} />
              <Route path="/issuer-submissions" element={<CarrierIssuerSubmissions />} />
              <Route path="/document-packages/send" element={<DocumentPackageSend />} />
              <Route path="/document-packages/:packageId" element={<DocumentPackageDetail />} />
              <Route path="/cmv" element={<CmvPage />} />
              <Route path="/cmv/daily" element={<CmvDailyPage />} />
              <Route path="/enforcement" element={<Enforcement />} />
              <Route path="/account" element={<Account />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/fleet" element={<FleetHome />} />
              <Route path="/fleet/drivers/:driverUserId/summary" element={<FleetDriverSummary />} />
            </Route>
            <Route path="/v/:token" element={<Viewer />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
